// Cognito Admin API for user management
import {
    CognitoUserPool,
    CognitoUser,
    CognitoUserAttribute
} from "amazon-cognito-identity-js";
import axios from 'axios';
import awsmobile from "../../aws-exports";

const BASE_URL = 'https://g1e3kx0ij7.execute-api.ap-south-1.amazonaws.com/testing';

const userPool = new CognitoUserPool({
    UserPoolId: awsmobile.aws_user_pools_id,
    ClientId: awsmobile.aws_user_pools_web_client_id,
});

/**
 * Create a new user in Cognito User Pool via Lambda API
 * @param {Object} userData - User data object
 * @param {string} userData.username - Username (email)
 * @param {string} userData.email - User email
 * @param {string} userData.password - Temporary password
 * @param {string} userData.name - User's full name
 * @param {string} userData.role - User role (admin/user)
 * @returns {Promise<Object>} - Result object with success status
 */
export const createUser = async (userData) => {
    try {
        const response = await axios.post(`${BASE_URL}/users`, {
            username: userData.username,
            email: userData.email,
            name: userData.name,
            password: userData.password,
            role: userData.role || 'user'
        });

        console.log("Create user API response:", response.data);

        let result = response.data;

        // Handle response - body might be a string that needs parsing
        if (typeof response.data.body === 'string') {
            result = JSON.parse(response.data.body);
        } else if (response.data.body) {
            result = response.data.body;
        }

        if (result.success) {
            return {
                success: true,
                data: result.user || {
                    username: userData.username,
                    email: userData.email,
                    name: userData.name,
                    role: userData.role || 'user'
                }
            };
        } else {
            return {
                success: false,
                error: result.error || "Failed to create user"
            };
        }
    } catch (error) {
        console.error("Error creating user:", error);

        let errorMessage = "Failed to create user";

        if (error.response?.data) {
            let errorData = error.response.data;
            if (typeof errorData.body === 'string') {
                try {
                    errorData = JSON.parse(errorData.body);
                } catch (e) {}
            }
            errorMessage = errorData.error || errorData.message || errorMessage;
        } else if (error.message) {
            errorMessage = error.message;
        }

        throw {
            success: false,
            error: errorMessage
        };
    }
};

/**
 * Get user role from Cognito session
 * @returns {Promise<string|null>} - User role or null
 */
export const getUserRole = () => {
    return new Promise((resolve) => {
        const cognitoUser = userPool.getCurrentUser();

        if (!cognitoUser) {
            resolve(null);
            return;
        }

        cognitoUser.getSession((err, session) => {
            if (err || !session.isValid()) {
                resolve(null);
                return;
            }

            cognitoUser.getUserAttributes((err, attributes) => {
                if (err) {
                    console.error("Error getting user attributes:", err);
                    resolve(null);
                    return;
                }

                const roleAttr = attributes.find(attr => attr.Name === 'custom:role');
                resolve(roleAttr ? roleAttr.Value : 'user');
            });
        });
    });
};

/**
 * Check if current user is admin
 * @returns {Promise<boolean>} - True if user is admin
 */
export const isAdmin = async () => {
    const role = await getUserRole();
    return role === 'admin';
};

/**
 * Get current authenticated user info
 * @returns {Promise<Object|null>} - User info or null
 */
export const getCurrentUserInfo = () => {
    return new Promise((resolve) => {
        const cognitoUser = userPool.getCurrentUser();

        if (!cognitoUser) {
            resolve(null);
            return;
        }

        cognitoUser.getSession((err, session) => {
            if (err || !session.isValid()) {
                resolve(null);
                return;
            }

            cognitoUser.getUserAttributes((err, attributes) => {
                if (err) {
                    console.error("Error getting user attributes:", err);
                    resolve(null);
                    return;
                }

                const userInfo = {};
                attributes.forEach(attr => {
                    userInfo[attr.Name] = attr.Value;
                });
                userInfo.username = cognitoUser.getUsername();

                resolve(userInfo);
            });
        });
    });
};

/**
 * Confirm user registration (for email verification)
 * @param {string} username - Username to confirm
 * @param {string} code - Verification code
 * @returns {Promise<Object>} - Result object
 */
export const confirmUser = (username, code) => {
    return new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({
            Username: username,
            Pool: userPool
        });

        cognitoUser.confirmRegistration(code, true, (err, result) => {
            if (err) {
                console.error("Error confirming user:", err);
                reject({
                    success: false,
                    error: err.message || "Failed to confirm user"
                });
                return;
            }

            resolve({
                success: true,
                data: result
            });
        });
    });
};

/**
 * Resend confirmation code
 * @param {string} username - Username to resend code to
 * @returns {Promise<Object>} - Result object
 */
export const resendConfirmationCode = (username) => {
    return new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({
            Username: username,
            Pool: userPool
        });

        cognitoUser.resendConfirmationCode((err, result) => {
            if (err) {
                console.error("Error resending code:", err);
                reject({
                    success: false,
                    error: err.message || "Failed to resend confirmation code"
                });
                return;
            }

            resolve({
                success: true,
                data: result
            });
        });
    });
};

/**
 * Get stored users from localStorage
 * @returns {Array} - Array of users
 */
const getStoredUsers = () => {
    try {
        const stored = localStorage.getItem('app_users');
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

/**
 * Save users to localStorage
 * @param {Array} users - Array of users to save
 */
const saveStoredUsers = (users) => {
    try {
        localStorage.setItem('app_users', JSON.stringify(users));
    } catch (error) {
        console.error("Error saving users to localStorage:", error);
    }
};

/**
 * Add a user to stored users list
 * @param {Object} user - User object to add
 */
export const addStoredUser = (user) => {
    const users = getStoredUsers() || [];
    const existingIndex = users.findIndex(u => u.username === user.username);
    if (existingIndex >= 0) {
        users[existingIndex] = { ...users[existingIndex], ...user };
    } else {
        users.unshift(user);
    }
    saveStoredUsers(users);
};

/**
 * Update a stored user's status
 * @param {string} username - Username to update
 * @param {string} status - New status
 */
export const updateStoredUserStatus = (username, status) => {
    const users = getStoredUsers() || [];
    const userIndex = users.findIndex(u => u.username === username);
    if (userIndex >= 0) {
        users[userIndex].status = status;
        saveStoredUsers(users);
    }
};

/**
 * Remove a user from stored users
 * @param {string} username - Username to remove
 */
export const removeStoredUser = (username) => {
    const users = getStoredUsers() || [];
    const filtered = users.filter(u => u.username !== username);
    saveStoredUsers(filtered);
};

/**
 * List all users from backend API
 * @returns {Promise<Object>} - Result object with users list
 */
export const listUsers = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/users`);
        console.log("Users API response:", response.data);

        let apiUsers = [];

        // Handle response - body might be a string that needs parsing
        if (response.data) {
            // If body is a string, parse it
            if (typeof response.data.body === 'string') {
                const parsedBody = JSON.parse(response.data.body);
                apiUsers = parsedBody.users || [];
            }
            // If body is an object
            else if (response.data.body?.users) {
                apiUsers = response.data.body.users;
            }
            // If users is directly in response
            else if (response.data.users) {
                apiUsers = response.data.users;
            }
        }

        // Save to localStorage for backup
        if (apiUsers.length > 0) {
            saveStoredUsers(apiUsers);
        }

        return {
            success: true,
            data: apiUsers
        };

    } catch (error) {
        console.error("Error fetching users from API:", error);

        // Fallback to localStorage
        const storedUsers = getStoredUsers() || [];
        return {
            success: true,
            data: storedUsers
        };
    }
};

/**
 * Delete a user (requires backend API with admin privileges)
 * @param {string} username - Username to delete
 * @returns {Promise<Object>} - Result object
 */
export const deleteUser = async (username) => {
    try {
        const response = await axios.delete(`${BASE_URL}/users/${username}`);

        let result = response.data;

        // Handle response - body might be a string that needs parsing
        if (typeof response.data.body === 'string') {
            result = JSON.parse(response.data.body);
        } else if (response.data.body) {
            result = response.data.body;
        }

        if (result.success) {
            // Only remove from localStorage if API deletion was successful
            removeStoredUser(username);
            return {
                success: true,
                data: result
            };
        } else {
            return {
                success: false,
                error: result.error || "Failed to delete user"
            };
        }
    } catch (error) {
        console.error("Error deleting user:", error);

        let errorMessage = "Failed to delete user";

        if (error.response?.data) {
            let errorData = error.response.data;
            if (typeof errorData.body === 'string') {
                try {
                    errorData = JSON.parse(errorData.body);
                } catch (e) {}
            }
            errorMessage = errorData.error || errorData.message || errorMessage;
        } else if (error.message) {
            errorMessage = error.message;
        }

        return {
            success: false,
            error: errorMessage
        };
    }
};

/**
 * Enable/Disable a user (requires backend API with admin privileges)
 * @param {string} username - Username to enable/disable
 * @param {boolean} enabled - Whether to enable or disable the user
 * @returns {Promise<Object>} - Result object
 */
export const setUserStatus = async (username, enabled) => {
    try {
        const response = await axios.put(`${BASE_URL}/users/${username}/status`, {
            enabled
        });

        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error("Error updating user status:", error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || "Failed to update user status"
        };
    }
};

/**
 * Initiate forgot password flow - sends verification code to user's email
 * @param {string} username - Username to reset password for
 * @returns {Promise<Object>} - Result object
 */
export const forgotPassword = (username) => {
    return new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({
            Username: username,
            Pool: userPool
        });

        cognitoUser.forgotPassword({
            onSuccess: (data) => {
                resolve({
                    success: true,
                    data: data,
                    message: "Verification code sent to your email"
                });
            },
            onFailure: (err) => {
                console.error("Error initiating forgot password:", err);
                reject({
                    success: false,
                    error: err.message || "Failed to send verification code"
                });
            }
        });
    });
};

/**
 * Confirm new password with verification code
 * @param {string} username - Username to reset password for
 * @param {string} verificationCode - Code received via email
 * @param {string} newPassword - New password to set
 * @returns {Promise<Object>} - Result object
 */
export const confirmForgotPassword = (username, verificationCode, newPassword) => {
    return new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({
            Username: username,
            Pool: userPool
        });

        cognitoUser.confirmPassword(verificationCode, newPassword, {
            onSuccess: () => {
                resolve({
                    success: true,
                    message: "Password reset successfully"
                });
            },
            onFailure: (err) => {
                console.error("Error confirming new password:", err);
                reject({
                    success: false,
                    error: err.message || "Failed to reset password"
                });
            }
        });
    });
};

/**
 * Find username by email address
 * @param {string} email - Email address to search for
 * @returns {Promise<Object>} - Result object with username if found
 */
export const findUsernameByEmail = async (email) => {
    try {
        const result = await listUsers();
        if (result.success) {
            const user = result.data.find(
                u => u.email?.toLowerCase() === email.toLowerCase()
            );
            if (user) {
                return {
                    success: true,
                    username: user.username
                };
            }
            return {
                success: false,
                error: "No user found with this email address"
            };
        }
        return {
            success: false,
            error: "Failed to search for user"
        };
    } catch (error) {
        console.error("Error finding username by email:", error);
        return {
            success: false,
            error: "Failed to search for user"
        };
    }
};

export default {
    createUser,
    getUserRole,
    isAdmin,
    getCurrentUserInfo,
    confirmUser,
    resendConfirmationCode,
    listUsers,
    deleteUser,
    setUserStatus,
    addStoredUser,
    updateStoredUserStatus,
    removeStoredUser,
    forgotPassword,
    confirmForgotPassword,
    findUsernameByEmail
};
